import { useCallback } from "react";
import { toast } from "sonner";
import { transformDataForExport } from "@/lib/utils/crm/relation-helpers";
import type { BaseEntity, SimpleTableConfig, EntityHooks } from "../../UniversalCRMTable/types";
import type { ExportFormat, ExportScope, ExportFormatType, EntityListData, GraphQLFilter } from "../types";
import { useFetchAllPages } from "./useFetchAllPages";
import { getExportFields, performFormatExport } from "./export-format-dispatch";
import { errorMessage } from "@/lib/utils/api/error-text";

interface FilterHelpers {
  convertToGraphQLFilter: (filters: GraphQLFilter) => GraphQLFilter;
  constructSearchFilter: (searchTerm: string) => GraphQLFilter;
  convertAdvancedFiltersToGraphQL: (advancedFilters: GraphQLFilter) => GraphQLFilter;
}

export function useExportHandlers<T extends BaseEntity>(
  entityName: string,
  config: SimpleTableConfig<T>,
  hooks: EntityHooks<T>,
  filterPreset: "user" | "all",
  appUser: { id: string } | null | undefined,
  debouncedSearchTerm: string,
  customFilters: GraphQLFilter,
  currentFilters: GraphQLFilter,
  advancedFilters: GraphQLFilter,
  data: EntityListData<T> | undefined,
  filterHelpers: FilterHelpers,
  isFetchingRef: React.MutableRefObject<boolean>,
  setExportProgress: (progress: number) => void,
  setShowExportModal: (show: boolean) => void,
  setIsExporting: (exporting: boolean) => void
) {
  const { convertToGraphQLFilter, constructSearchFilter, convertAdvancedFiltersToGraphQL } =
    filterHelpers;

  const fetchAllPages = useFetchAllPages<T>({
    entityName,
    hooks,
    filterPreset,
    appUser,
    debouncedSearchTerm,
    customFilters,
    currentFilters,
    advancedFilters,
    data,
    convertToGraphQLFilter,
    constructSearchFilter,
    convertAdvancedFiltersToGraphQL,
    isFetchingRef,
    setExportProgress,
  });

  const exportCurrentPage = useCallback(
    async (exportFormat: ExportFormat, _format: ExportFormatType = "structured") => {
      const rawExportData = data?.data || [];
      if (!rawExportData.length) {
        toast.error("No data to export");
        return;
      }
      const exportData = transformDataForExport(rawExportData);
      const fields = getExportFields(config);
      await performFormatExport(exportData, exportFormat, fields, entityName);
      toast.success(`Exported ${exportData.length} records to ${exportFormat.toUpperCase()}`);
    },
    [data?.data, config, entityName]
  );

  const exportAllFiltered = useCallback(
    async (exportFormat: ExportFormat, _format: ExportFormatType = "structured") => {
      try {
        setExportProgress(10);
        const allData = await fetchAllPages();
        setExportProgress(60);

        if (!allData.length) {
          toast.error("No data to export");
          setExportProgress(0);
          return;
        }

        setExportProgress(70);
        const exportData = transformDataForExport(allData);
        setExportProgress(85);
        const fields = getExportFields(config);

        await performFormatExport(exportData, exportFormat, fields, entityName, setExportProgress);

        setExportProgress(100);
        toast.success(`Exported ${allData.length} records to ${exportFormat.toUpperCase()}`);
        setTimeout(() => setExportProgress(0), 1000);
      } catch (error) {
        toast.error("Export failed: " + errorMessage(error));
        setExportProgress(0);
      }
    },
    [config, entityName, fetchAllPages, setExportProgress]
  );

  const handleExportWithScope = useCallback(
    async (scope: ExportScope, exportFormat: ExportFormat, format?: ExportFormatType) => {
      setIsExporting(true);
      try {
        if (scope === "current-page") {
          await exportCurrentPage(exportFormat, format || "structured");
        } else {
          await exportAllFiltered(exportFormat, format || "structured");
        }
      } finally {
        setShowExportModal(false);
        setIsExporting(false);
      }
    },
    [exportCurrentPage, exportAllFiltered, setIsExporting, setShowExportModal]
  );

  const handleExportItems = useCallback(() => {
    setShowExportModal(true);
  }, [setShowExportModal]);

  return {
    fetchAllPages,
    exportCurrentPage,
    exportAllFiltered,
    handleExportWithScope,
    handleExportItems,
  };
}
